import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";

import ControlledPhoneInputSeparatedLabel from "../../../../components/controlled/ControlledPhoneInputSeparatedLabel/index.jsx";
import ControlledRadioGroupSeparatedLabel from "../../../../components/controlled/ControlledRadioGroupSeparatedLabel/index.jsx";
import ControlledSelectSeparatedLabel from "../../../../components/controlled/ControlledSelectSeparatedLabel/index.jsx";
import ControlledTextField from "../../../../components/controlled/ControlledTextField/index.jsx";
import ControlledTextFieldSeparatedLabel from "../../../../components/controlled/ControlledTextFieldSeparatedLabel/index.jsx";

import { useGetCurrentCompanyBranchesQuery } from "../../../branches/branchesApiSlice";
import { useGetCurrentCompanyAdminsQuery } from "../../../companies/companiesApiSlice.js";
import { useUpdateCurrentUserMutation } from "../../userApiSlice";

import { Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import UserDataSkeleton from "../../../../components/skeletons/UserDataSkeleton/index.jsx";
import { StyledTimePicker } from "../../../../components/StyledTimePicker/index.jsx";
import { GET_USER_ROLES_OPTIONS } from "../../../../constants.js";
import { getFormSchema } from "./formSchema";

const Form = ({ user }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { data: branches, isLoading } = useGetCurrentCompanyBranchesQuery();
	const { data: admins, isLoading: isLoadingAdmins } =
		useGetCurrentCompanyAdminsQuery();
	const [update, { isLoading: isUpdating }] = useUpdateCurrentUserMutation();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setError,
		watch,
	} = useForm({
		defaultValues: {
			company: user.companyName,
			firstName: user.first_name,
			lastName: user.last_name,
			email: user.email,
			phone: user.phone_number,
			branch: user.company_branch_id,
			role: user.role,
			emailFooter: user.footer || "",
			showPhoneAndEmailInMailingGroups: user.show_phone_and_email,
			notificationCargoProposition: user.notification_cargo_proposition,
			shouldUseCustomTimes: user.should_use_custom_times,
			loadFrom: user.preferences.times.loadFrom,
			loadTo: user.preferences.times.loadTo,
			unloadFrom: user.preferences.times.unloadFrom,
			unloadTo: user.preferences.times.unloadTo,
		},
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const watchShouldUseCustomTimes = watch("shouldUseCustomTimes");

	const onSubmit = async ({
		company,
		firstName,
		lastName,
		email,
		phone,
		branch,
		role,
		emailFooter,
		showPhoneAndEmailInMailingGroups,
		notificationCargoProposition,
		password,
		newPassword,
		confirmPassword,
		shouldUseCustomTimes,
		loadFrom,
		loadTo,
		unloadFrom,
		unloadTo,
	}) => {
		const parsedLoadFrom = new Date(loadFrom);
		parsedLoadFrom.setSeconds(0);
		parsedLoadFrom.setMilliseconds(0);
		const parsedLoadTo = new Date(loadTo);
		parsedLoadTo.setSeconds(0);
		parsedLoadTo.setMilliseconds(0);
		const parsedUnloadFrom = new Date(unloadFrom);
		parsedUnloadFrom.setSeconds(0);
		parsedUnloadFrom.setMilliseconds(0);
		const parsedUnloadTo = new Date(unloadTo);
		parsedUnloadTo.setSeconds(0);
		parsedUnloadTo.setMilliseconds(0);

		await update({
			company,
			firstName,
			lastName,
			email,
			phone: phone.replaceAll(" ", ""),
			branch,
			role,
			...(emailFooter && { emailFooter: `${emailFooter}` }),
			showPhoneAndEmailInMailingGroups,
			notificationCargoProposition: notificationCargoProposition ? 1 : 0,
			...(password && { password }),
			...(newPassword && { newPassword }),
			...(confirmPassword && { confirmPassword }),
			shouldUseCustomTimes,
			...(loadFrom ? { loadFrom: parsedLoadFrom } : { loadFrom: null }),
			...(loadTo ? { loadTo: parsedLoadTo } : { loadTo: null }),
			...(unloadFrom ? { unloadFrom: parsedUnloadFrom } : { unloadFrom: null }),
			...(unloadTo ? { unloadTo: parsedUnloadTo } : { unloadTo: null }),
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_data"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				enqueueSnackbar(err.message, {
					variant: "error",
					preventDuplicate: true,
				});
			});
		}
	}, [errors, enqueueSnackbar]);

	if (isLoading || isLoadingAdmins) {
		return <UserDataSkeleton />;
	}

	return (
		<Box
			component="form"
			noValidate
			onSubmit={handleSubmit(onSubmit)}
			sx={{ p: 0 }}
		>
			<Grid container spacing={5}>
				<Grid
					size={{
						xs: 12,
						md: 6,
					}}
				>
					<Grid container spacing={2}>
						<Grid size={12}>
							<ControlledTextFieldSeparatedLabel
								control={control}
								name="company"
								label={t("company")}
								error={!!errors?.company}
								labelXs={6}
								fieldXs={6}
								disabled
							/>
						</Grid>
						<Grid size={12}>
							<ControlledTextFieldSeparatedLabel
								control={control}
								name="firstName"
								label={t("first_name_label")}
								error={!!errors?.name}
								labelXs={6}
								fieldXs={6}
								disabled={user.role === "user"}
							/>
						</Grid>
						<Grid size={12}>
							<ControlledTextFieldSeparatedLabel
								control={control}
								name="lastName"
								label={t("surname_label")}
								error={!!errors?.name}
								labelXs={6}
								fieldXs={6}
								disabled={user.role === "user"}
							/>
						</Grid>
						<Grid size={12}>
							<ControlledTextFieldSeparatedLabel
								control={control}
								name="email"
								type="email"
								autoComplete="email"
								label={t("email_and_login_label")}
								error={!!errors?.email}
								labelXs={6}
								fieldXs={6}
							/>
						</Grid>
						<Grid size={12}>
							<ControlledPhoneInputSeparatedLabel
								control={control}
								name="phone"
								type="tel"
								autoComplete="tel"
								label={t("phone_label")}
								error={!!errors?.phone}
								labelXs={6}
								fieldXs={6}
							/>
						</Grid>
						<Grid size={12}>
							<ControlledSelectSeparatedLabel
								control={control}
								name="branch"
								label={t("branch")}
								options={branches.map((branch) => {
									return { value: branch.id, label: branch.name };
								})}
								error={!!errors?.branch}
								labelXs={6}
								fieldXs={6}
								disabled={user.role === "user"}
							/>
						</Grid>
						<Grid size={12}>
							<ControlledRadioGroupSeparatedLabel
								className="user-role-radio-group"
								control={control}
								name="role"
								label={t("permission_type")}
								options={GET_USER_ROLES_OPTIONS(t)}
								labelXs={6}
								fieldXs={6}
								disabled={
									user.role === "user" || user.role === "branch-manager"
								}
							/>
						</Grid>
						<Grid
							sx={{
								display: "flex",
								justifyContent: "start",
								alignItems: "center",
							}}
							size={6}
						>
							{t("mailing")}:
						</Grid>
						<Grid size={6}>
							<Controller
								control={control}
								name="showPhoneAndEmailInMailingGroups"
								render={({ field: { onChange, value } }) => (
									<FormControlLabel
										error={errors?.showPhoneAndEmailInMailingGroups ? 1 : 0}
										control={
											<Checkbox
												sx={{ py: 0 }}
												name="showPhoneAndEmailInMailingGroups"
												onChange={onChange}
												checked={!!value}
											/>
										}
										label={
											<span style={{ whiteSpace: "nowrap" }}>
												{t("add_my_phone_and_email_to_offers_in_mailing")}
											</span>
										}
									/>
								)}
							></Controller>
						</Grid>
						<Grid
							sx={{
								display: "flex",
								justifyContent: "start",
								alignItems: "start",
								mt: "6px",
							}}
							size={6}
						>
							{t("change_password")}:
						</Grid>
						<Grid sx={{ mt: "6px" }} size={6}>
							<ControlledTextField
								control={control}
								name="password"
								type="password"
								label={t("current_password")}
								margin="normal"
								sx={{ mt: 0 }}
								error={!!errors?.password}
							/>
							<ControlledTextField
								control={control}
								name="newPassword"
								type="password"
								label={t("new_password")}
								margin="normal"
								sx={{ mt: 1 }}
								error={!!errors?.newPassword}
							/>
							<ControlledTextField
								control={control}
								name="confirmPassword"
								type="password"
								label={t("confirm_password_label")}
								margin="normal"
								sx={{ mt: 1 }}
								error={!!errors?.confirmPassword}
							/>
						</Grid>
						<Grid
							sx={{
								display: "flex",
								justifyContent: "start",
								alignItems: "start",
							}}
							size={6}
						>
							{t("notifications")}:
						</Grid>
						<Grid size={6}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									paddingBottom: "20px",
									gap: "8px",
								}}
							>
								<Controller
									control={control}
									name="notificationCargoProposition"
									render={({ field: { onChange, value } }) => (
										<FormControlLabel
											error={errors?.notificationCargoProposition ? 1 : 0}
											control={
												<Checkbox
													sx={{ py: 0 }}
													name="notificationCargoProposition"
													onChange={onChange}
													checked={!!value}
												/>
											}
											label={
												<span style={{ whiteSpace: "nowrap" }}>
													{t("notify_me_about_new_propositions")}
												</span>
											}
										/>
									)}
								></Controller>
							</Box>
						</Grid>
						<Grid
							sx={{
								display: "flex",
								justifyContent: "start",
								alignItems: "start",
								paddingTop: "26px",
							}}
							size={6}
						>
							{t("default_loading_unloading_hours")}:
						</Grid>
						<Grid size={6}>
							<Box
								sx={{
									position: "relative",
									border: "1px solid #c4c4c4",
									borderRadius: "4px",
									padding: "14px 20px 10px",
									left: "-16px",
									width: "calc(100% + 16px)",
								}}
							>
								<Box
									sx={{
										position: "absolute",
										top: "-12px",
										backgroundColor: "white",
										left: "10px",
										paddingLeft: "5px",
									}}
								>
									<Controller
										control={control}
										name="shouldUseCustomTimes"
										render={({ field: { onChange, value } }) => (
											<FormControlLabel
												control={
													<Checkbox
														sx={{ py: 0 }}
														name="shouldUseCustomTimes"
														onChange={onChange}
														checked={!!value}
													/>
												}
												label={
													<span style={{ whiteSpace: "nowrap" }}>
														{t("i_want_to_set_default_hours")}
													</span>
												}
											/>
										)}
									></Controller>
								</Box>
								<Box
									sx={{
										alignItems: "center",
										display: "flex",
										position: "relative",
										gap: "4px",
										height: "100%",
									}}
								>
									<Typography sx={{ mt: "8px", mr: "10px", width: "80px" }}>
										{t("load")}:{" "}
									</Typography>
									<Controller
										control={control}
										name="loadFrom"
										render={({ field: { onChange, value } }) => {
											return (
												<StyledTimePicker
													disabled={!watchShouldUseCustomTimes}
													label={t("hours_label") + " *"}
													value={value || null}
													onChange={onChange}
													error={!!errors?.loadFrom}
												/>
											);
										}}
									/>
									<span>-</span>
									<Controller
										control={control}
										name="loadTo"
										// defaultValue="day"
										render={({ field: { onChange, value } }) => {
											return (
												<StyledTimePicker
													disabled={!watchShouldUseCustomTimes}
													label={t("hours_label")}
													value={value || null}
													onChange={onChange}
													error={!!errors?.loadTo}
												/>
											);
										}}
									/>
								</Box>
								<Box
									sx={{
										alignItems: "center",
										display: "flex",
										position: "relative",
										gap: "4px",
										height: "100%",
									}}
								>
									<Typography sx={{ mt: "8px", mr: "10px", width: "80px" }}>
										{t("unload")}:{" "}
									</Typography>
									<Controller
										control={control}
										name="unloadFrom"
										render={({ field: { onChange, value } }) => {
											return (
												<StyledTimePicker
													disabled={!watchShouldUseCustomTimes}
													label={t("hours_label") + " *"}
													value={value || null}
													onChange={onChange}
													error={!!errors?.unloadFrom}
												/>
											);
										}}
									/>
									<span>-</span>
									<Controller
										control={control}
										name="unloadTo"
										// defaultValue="day"
										render={({ field: { onChange, value } }) => {
											return (
												<StyledTimePicker
													disabled={!watchShouldUseCustomTimes}
													label={t("hours_label")}
													value={value || null}
													onChange={onChange}
													error={!!errors?.unloadTo}
												/>
											);
										}}
									/>
								</Box>
							</Box>
						</Grid>
						<Grid size={6}></Grid>
						<Grid size={6}>
							<Box sx={{ display: "flex", justifyContent: "start" }}>
								<Button loading={isUpdating} type="submit" variant="contained">
									{t("save")}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Grid>
				<Grid
					size={{
						xs: 12,
						md: 6,
					}}
				>
					<Grid container spacing={2}>
						<Grid size={12}>
							<Box
								sx={{
									maxWidth: "540px",
									border: "1px solid #FFB15D",
									color: "#7F7F7F",
									px: 5,
									py: 3,
								}}
							>
								<p style={{ marginTop: 0 }}>
									{t("user_management_permissions_info")}
								</p>
								<p>{t("administrators")}:</p>
								<ul style={{ marginBottom: 0 }}>
									{admins.data.map((admin) => {
										return <li key={admin}>{admin}</li>;
									})}
								</ul>
							</Box>
						</Grid>
						<Grid size={12}>
							<Box sx={{ height: "176px" }}></Box>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};

export default Form;
